import { Button } from '@/components/shadcn/button'
import { ApplicationForm } from '../Forms/ApplicationForm'
import { useCurrentApplication } from '../useCurrentApplication'
import { IconEditOutline } from '@icons/IconEditOutline'
import { DynamicPictogram } from '@/components/pictograms/DynamicPictogram'
import { Separator } from '@/components/shadcn/separator'
import { Badge } from '@/components/shadcn/badge'
import { IconV2FileOutline } from '@icons/IconV2FileOutline'
import { useRestClient } from '@/contexts/QueryProvider'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { getInitials } from '@/lib/utils'
import clsx from 'clsx'
import { MaintainAppAdminsForm } from '../Forms/MaintainAppAdminsForm'

const BADGE_VARIANT_MAP = {
  Live: 'success',
  Developing: 'warning',
  'Coming Soon': 'info',
} as const

export const AppDetailsBanner = () => {
  const application = useCurrentApplication()
  const { appName, description, status, fileTypes, maxFileSizeMb, pictureUrl } = application
  return (
    <div className="relative flex shrink-0 flex-row justify-between gap-x-20 gap-y-4 border-b pb-4">
      <div className="flex shrink-0 gap-5">
        {pictureUrl && (
          <>
            <DynamicPictogram gradient pictogramName={pictureUrl} className="size-28" />
            <div className="self-stretch py-2">
              <Separator orientation="vertical" />
            </div>
          </>
        )}
        <div className="flex flex-col justify-between py-1">
          <div>
            <div className="flex items-center gap-5">
              <h3 className="text-2xl font-bold">{appName}</h3>
              <Badge
                className="h-5 font-bold uppercase"
                variant={BADGE_VARIANT_MAP[status as keyof typeof BADGE_VARIANT_MAP]}
              >
                {status}
              </Badge>
            </div>
            <div className="text-sm font-light">{description}</div>
          </div>
          <div className="flex items-center gap-2">
            <div>Admins:</div>
            <AppAdminsListV2 />
            <MaintainAppAdminsForm
              selectedApp={application}
              sheetTitle={`${appName} > Manage administrators`}
              sheetDescription="Manage the administrators of the application."
              sheetTriggerTooltip="Manage application administrators"
              sheetTrigger={
                <Button variant="ghost" size="icon">
                  <IconEditOutline />
                </Button>
              }
            />
          </div>
        </div>
      </div>
      <div className="flex items-end px-10">
        <div className="flex flex-col rounded-xl border bg-background px-5 pb-3 pt-2 font-light">
          <div className="flex items-end gap-2">
            <IconV2FileOutline className="inline-block size-6" />
            <span>{`(Max ${maxFileSizeMb}Mb)`}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1 pt-2">
            {fileTypes.split('/').map((type) => (
              <Badge variant="outline" className="h-4 border-none bg-muted" key={type}>
                {type}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      <ApplicationForm
        selectedApp={application}
        sheetTitle={`${appName} > Edit details`}
        sheetDescription="Edit the details of the application."
        sheetTriggerTooltip="Edit application details"
        sheetTrigger={
          <Button variant="ghost" size="icon" className="absolute right-0 top-0">
            <IconEditOutline />
          </Button>
        }
      />
    </div>
  )
}

function AppAdminsListV2() {
  const application = useCurrentApplication()
  const { applicationId } = application
  const { getApplicationsApiListApplicationAdmins } = useRestClient()
  const { data: currentAppAdmins } = getApplicationsApiListApplicationAdmins.useSuspenseQuery({
    query: { applicationId },
  })

  if (!currentAppAdmins.length) {
    return <span className="text-sm text-muted-foreground">None</span>
  }

  return (
    <div className="flex shrink-0 -space-x-2">
      {currentAppAdmins.map((admin, index) => {
        const name = `${admin.givenName} ${admin.familyName}`

        return (
          <TooltipButton key={admin.id} tooltip={name}>
            <span className="inline-flex">
              <Avatar className="ring-2 ring-background">
                <AvatarFallback
                  className={clsx(
                    'bg-accent text-accent-foreground',
                    index % 2 !== 0 && 'bg-secondary text-secondary-foreground'
                  )}
                >
                  {getInitials(name)}
                </AvatarFallback>
              </Avatar>
            </span>
          </TooltipButton>
        )
      })}
    </div>
  )
}
